import React, { createContext, useContext, ReactNode } from "react";
import { useAuthentication } from "./hooks/useAuthentication";

import type { AppUser } from "./hooks/useAuthentication";
import type { LoginOptions } from "./oidc";

interface AuthContextType {
  user: AppUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionExpired: boolean;
  login: (options?: LoginOptions) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  updateUser: (userData: Partial<AppUser>) => void;
  checkAuthStatus: () => Promise<{
    isAuthenticated: boolean;
    isLoading: boolean;
  }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const authentication = useAuthentication();

  const value: AuthContextType = {
    user: authentication.user,
    isAuthenticated: authentication.isAuthenticated,
    isLoading: authentication.isLoading,
    sessionExpired: authentication.sessionExpired,
    login: authentication.login,
    logout: authentication.logout,
    getAccessToken: authentication.getAccessToken,
    updateUser: authentication.updateUser,
    checkAuthStatus: authentication.checkAuthStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(
      "useAuth muss innerhalb eines AuthProviders verwendet werden",
    );
  }
  return context;
};
